import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import PropTypes from 'prop-types';

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { useToast } from '@/common-utils/hooks/use-toast';
import actionsPosTerminals from '../../../api/actionsPosTerminals';
import { Modal, ModalBody, ModalContent, ModalTrigger } from '@/common-utils/components/ui/modal';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import { cn } from '@/common-utils/libraries/tailwind/utils';
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import { nsTerminal } from '@/app/i18n/namespaces';

/**
 * @param {JSX.Element} trigger - Element that triggers modal opening
 * @returns {JSX.Element} ModalUpdateLabels Component
 */
function ModalUpdateLabels({ trigger, isOpen, setIsOpen, posTerminal, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // Validation Schema
    const formSchema = z.object({
        label: z.string().max(100), // Max 100 characters
        customLabel: z.string().max(100)
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            label: posTerminal[subscriptionFields.POS_TERMINAL.LABEL.key] ?? '',
            customLabel: posTerminal[subscriptionFields.POS_TERMINAL.CUSTOM_LABEL.key] ?? ''
        }
    });

    // API Calls
    const onSuccess = () => {
        setLaunchRefetch(true);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_labels.success' namespace={nsTerminal} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_labels.error' namespace={nsTerminal} />,
            variant: 'danger'
        });
    };
    const submitUpdateLabels = actionsPosTerminals.updatePosTerminalLabels(onSuccess, onError);

    // Functions
    const onSubmit = (data) => {
        submitUpdateLabels.mutate({
            posTerminalId: posTerminal.uuid,
            posTerminalLabels: {
                [subscriptionFields.POS_TERMINAL.LABEL.key]: data.label,
                [subscriptionFields.POS_TERMINAL.CUSTOM_LABEL.key]: data.customLabel
            }
        });
    };

    // Modal Content
    const content = (
        <ModalContent className={cn('max-w-lg')}>
            <HeaderTitle title='modal.update_labels.title' namespace={nsTerminal} />
            <ModalBody>
                <Form {...form} onSubmit={form.handleSubmit(onSubmit)}>
                    <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.update_labels.label' namespace={nsTerminal} />
                    </FormLabel>
                    <FormField
                        name='label'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        entity='modal.update_labels.placeholder'
                                        namespace={nsTerminal}
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormLabel htmlFor='customLabel' className='block text-sm font-medium text-gray-700 mt-4'>
                        <Entity entity='modal.update_labels.custom_label' namespace={nsTerminal} />
                    </FormLabel>
                    <FormField
                        name='customLabel'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        entity='modal.update_labels.placeholder'
                                        namespace={nsTerminal}
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </Form>
            </ModalBody>
            <FooterForm
                onSubmit={form.handleSubmit(onSubmit)}
                isSubmitting={submitUpdateLabels.isLoading}
                isSubmitDisabled={!form.formState.isValid}
            />
        </ModalContent>
    );

    return trigger ? (
        <Modal>
            <ModalTrigger>{trigger}</ModalTrigger>
            {content}
        </Modal>
    ) : (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            {content}
        </Modal>
    );
}
ModalUpdateLabels.propTypes = {
    trigger: PropTypes.element,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    posTerminal: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func
};
export default ModalUpdateLabels;
